import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserRole } from '@/integrations/supabase/types';

export interface JWTClaims {
  organization_id: string | null;
  role: UserRole;
  organization_name: string | null;
  is_coordi_team: boolean;
  is_personal: boolean; // True if user belongs to a personal organization (individual user)
  claims_version: number;
  claims_updated_at: string;
}

export interface ExtendedJWTClaims extends JWTClaims {
  isFounder: boolean;
  isAdmin: boolean;
  isIndividualUser: boolean; // Convenience flag derived from is_personal
}

export interface UseJWTClaimsResult {
  claims: ExtendedJWTClaims | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  isStale: boolean;
}

interface ClaimsMetadata {
  organization_id: string | null;
  role: UserRole;
  organization_name?: string | null;
  is_coordi_team?: boolean;
  is_personal?: boolean;
  claims_version?: number;
  claims_updated_at?: string;
}

/**
 * Hook to access JWT custom claims from the current user's session
 *
 * This hook extracts claims directly from the JWT token stored in app_metadata,
 * eliminating the need for database queries to get user organization and role.
 *
 * @returns {UseJWTClaimsResult} Claims data, loading state, error, and refresh function
 *
 * @example
 * ```tsx
 * const { claims, loading, error, refresh, isStale } = useJWTClaims();
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (isStale) {
 *   // Optionally trigger refresh
 *   refresh();
 * }
 *
 * return <div>Organization: {claims.organization_name}</div>;
 * ```
 */
export function useJWTClaims(): UseJWTClaimsResult {
  const [claims, setClaims] = useState<ExtendedJWTClaims | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);

  /**
   * Extract typed claims from Supabase `user.app_metadata`.
   *
   * Validates presence of critical fields (role) and tolerates
   * nullable `organization_id` for individual users. Also maps
   * convenience booleans (founder/admin/individual).
   */
  const extractClaims = useCallback((user: { app_metadata?: unknown } | null): ExtendedJWTClaims | null => {
    if (!user) return null;

    const appMetadata = (user.app_metadata || {}) as Partial<ClaimsMetadata>;

    // Validate required claims
    if (!appMetadata.role) {
      console.warn('[useJWTClaims] Missing role claim in app_metadata');
      return null;
    }

    // organization_id is nullable for individual users
    if (!('organization_id' in appMetadata)) {
      console.warn('organization_id not found in app_metadata');
    }
    // Don't throw - null is valid for individual users

    const baseClaims: JWTClaims = {
      organization_id: appMetadata.organization_id,
      role: appMetadata.role as UserRole,
      organization_name: appMetadata.organization_name || null,
      is_coordi_team: appMetadata.is_coordi_team === true,
      is_personal: appMetadata.is_personal === true, // Individual users have personal organizations
      claims_version: appMetadata.claims_version || 1,
      claims_updated_at: appMetadata.claims_updated_at || new Date().toISOString(),
    };

    const extendedClaims: ExtendedJWTClaims = {
      ...baseClaims,
      isFounder: baseClaims.is_coordi_team,
      isAdmin: baseClaims.role === 'admin',
      // Individual users are identified by is_personal flag (personal organization)
      // Fallback to null org_id check for backwards compatibility with legacy users
      isIndividualUser: baseClaims.is_personal || baseClaims.organization_id === null,
    };

    return extendedClaims;
  }, []);

  /**
   * Load current session claims and compute staleness.
   *
   * Marks claims as stale if `claims_updated_at` is older than 5 minutes,
   * allowing consumers to optionally invoke a refresh.
   */
  const loadClaims = useCallback(async () => {
    console.log('[useJWTClaims] loadClaims starting');
    try {
      setLoading(true);
      setError(null);

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('[useJWTClaims] getSession result:', { hasSession: !!session, hasUser: !!session?.user, sessionError });

      if (sessionError) throw sessionError;

      if (!session?.user) {
        console.log('[useJWTClaims] No session/user, setting claims to null');
        setClaims(null);
        return;
      }

      const extractedClaims = extractClaims(session.user);
      console.log('[useJWTClaims] Extracted claims:', extractedClaims);
      if (!extractedClaims) {
        setClaims(null);
        return;
      }
      setClaims(extractedClaims);

      // Check staleness (claims older than 5 minutes)
      if (extractedClaims?.claims_updated_at) {
        const claimsAge = Date.now() - new Date(extractedClaims.claims_updated_at).getTime();
        setIsStale(claimsAge > 5 * 60 * 1000);
      }
    } catch (err) {
      console.error('[useJWTClaims] Error loading claims:', err);
      setError(err instanceof Error ? err : new Error('Failed to load JWT claims'));
      setClaims(null);
    } finally {
      console.log('[useJWTClaims] loadClaims finished, setting loading=false');
      setLoading(false);
    }
  }, [extractClaims]);

  /**
   * Force-refresh the Supabase session and re-extract claims.
   *
   * Clears staleness indicator on success.
   */
  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) throw refreshError;

      if (session?.user) {
        const extractedClaims = extractClaims(session.user);
        setClaims(extractedClaims);
        setIsStale(false);
      }
    } catch (err) {
      console.error('[useJWTClaims] Error refreshing claims:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh JWT claims'));
    } finally {
      setLoading(false);
    }
  }, [extractClaims]);

  useEffect(() => {
    loadClaims();

    // Keep claims in sync with auth lifecycle events.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          if (session?.user) {
            try {
              const extractedClaims = extractClaims(session.user);
              setClaims(extractedClaims);
              setIsStale(false);
              setError(null);
            } catch (err) {
              setError(err instanceof Error ? err : new Error('Invalid claims'));
              setClaims(null);
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setClaims(null);
          setError(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [loadClaims, extractClaims]);

  return { claims, loading, error, refresh, isStale };
}
